// ABOUTME: Layout route for /skills/* — provides the dashboard-style header,
// ABOUTME: a filter bar with search + CTA, and (for superadmins) a Skills/Workflows tab.

import { SkillsFilterContext } from "@/components/skills/SkillsFilterContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/_authenticated/skills")({
  component: SkillsLayout,
});

type ActiveTab = "skills" | "workflows";

function SkillsLayout() {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const [search, setSearch] = useState("");
  const isSuperAdmin = useSuperAdmin();

  const isSharedPage = pathname.startsWith("/skills/shared");
  const isWorkflowsPage = pathname.startsWith("/skills/workflows");

  // Hide the filter/tab bar on full-screen editor surfaces:
  //   /skills/new, /skills/$skillId, /skills/workflows/new, /skills/workflows/$id
  const isEditorPage =
    pathname === "/skills/new" ||
    /^\/skills\/workflows\/(new|[^/]+)$/.test(pathname) ||
    (pathname.startsWith("/skills/") &&
      pathname !== "/skills/" &&
      pathname !== "/skills/shared" &&
      pathname !== "/skills/shared/" &&
      pathname !== "/skills/workflows" &&
      pathname !== "/skills/workflows/" &&
      !pathname.startsWith("/skills/workflows/"));

  const activeTab: ActiveTab = isWorkflowsPage ? "workflows" : "skills";

  const { title, subtitle } = useMemo(() => {
    if (isSharedPage) {
      return {
        title: "Shared Skills",
        subtitle:
          "Skills your teammates have shared with you. View and launch them from the new-conversation pane.",
      };
    }
    return {
      title: "Skills",
      subtitle:
        "Save reusable prompts and launch them from the new-conversation pane. Share with teammates by setting visibility.",
    };
  }, [isSharedPage]);

  const ctxValue = useMemo(() => ({ search, setSearch }), [search]);

  return (
    <SkillsFilterContext.Provider value={ctxValue}>
      <div className="flex flex-1 flex-col overflow-auto">
        {!isEditorPage && (
          <>
            <div className="border-b border-border">
              <div className="flex items-center gap-4 p-4 pl-14 md:pl-4">
                <h1 className="text-xl font-semibold h-10 flex items-center">
                  {title}
                </h1>
              </div>
              <div className="px-4 pb-3 text-sm text-muted-foreground">
                {subtitle}
              </div>
            </div>

            <div className="border-b border-border px-4 py-3 flex flex-wrap gap-3 items-center">
              {isSuperAdmin && !isSharedPage && (
                <TabStrip
                  active={activeTab}
                  onChange={(t) => {
                    void navigate({
                      to: t === "skills" ? "/skills" : "/skills/workflows",
                    });
                  }}
                />
              )}
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  activeTab === "workflows"
                    ? "Search workflows..."
                    : "Search skills..."
                }
                className="h-9 w-64"
              />
              {!isSharedPage && (
                <Button
                  className="ml-auto"
                  onClick={() => {
                    if (activeTab === "workflows") {
                      void navigate({ to: "/skills/workflows/new" });
                    } else {
                      void navigate({ to: "/skills/new" });
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {activeTab === "workflows" ? "New Workflow" : "Add Skill"}
                </Button>
              )}
            </div>
          </>
        )}

        <Outlet />
      </div>
    </SkillsFilterContext.Provider>
  );
}

function TabStrip({
  active,
  onChange,
}: {
  active: ActiveTab;
  onChange: (next: ActiveTab) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-border bg-muted p-0.5 text-sm">
      <button
        type="button"
        onClick={() => onChange("skills")}
        className={`rounded-[5px] px-2.5 py-1 ${
          active === "skills"
            ? "bg-background font-medium shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Skills
      </button>
      <button
        type="button"
        onClick={() => onChange("workflows")}
        className={`rounded-[5px] px-2.5 py-1 ${
          active === "workflows"
            ? "bg-background font-medium shadow-sm"
            : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Workflows
      </button>
    </div>
  );
}
